import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useStore } from '../store/store';
import ChapterSummary from './ChapterSummary';
import { Tooltip } from "@radix-ui/themes";
import { Flex } from '@radix-ui/themes';
import avatar from '../assets/avatar4.png';

export const Novel = () => {
    const { novelId } = useParams();
    const [novel, setNovel] = useState(null);
    const [state, actions] = useStore();
    const [isCreating, setIsCreating] = useState(false);
    const [justCreated, setJustCreated] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        // Fetch novel data
        const fetchNovel = async () => {
            const result = await api.getNovelById(novelId);
            const novel = result.novel;

            const [characterResult, locationsResult, loreResult, chatsResult, notesResult] = await Promise.all([
                api.getCharactersByNovelId(novelId),
                api.getLocationsByNovelId(novelId),
                api.getLoreItemsByNovelId(novelId),
                api.getChatsByNovel(novelId),
                api.getNotesByNovelId(novelId)
            ]);

            novel.characters = characterResult.characters;
            novel.locations = locationsResult.locations;
            novel.lore = loreResult.loreItems;
            novel.chats = chatsResult.chats;
            novel.notes = notesResult.notes;
            
            setNovel(novel);
            actions.setCurrentNovel(novel);
        };

        fetchNovel();
    }, [novelId]);

    // Effect to scroll to bottom when a new chapter is created
    useEffect(() => {
        if (justCreated && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
            setJustCreated(false);
        }
    }, [justCreated]);

    const createEmptyChapter = async () => {
        if (isCreating) return; // Prevent multiple clicks
        
        setIsCreating(true);
        try {
            // Create a temporary chapter for immediate UI feedback
            const tempChapter = {
                id: `temp-${Date.now()}`,
                chapter_number: ((novel?.chapters?.length || 0) > 0 ? 
                    Math.max(...novel.chapters.map(c => c.chapter_number)) + 1 : 1),
                title: `Chapter ${(novel?.chapters?.length || 0) + 1}`,
                content: ""
            };
            
            // Update local state immediately for better UX
            const updatedNovel = { ...novel };
            if (!updatedNovel.chapters) updatedNovel.chapters = [];
            updatedNovel.chapters.push(tempChapter);
            setNovel(updatedNovel);
            setJustCreated(true);
            
            // Then save to the database
            const response = await api.createEmptyChapter(novelId);
            
            if (response.success) {
                // Update novel in store with latest data from server
                const refreshedNovel = await api.getNovelById(novelId);
                if (refreshedNovel.success) {
                    setNovel(refreshedNovel.novel);
                    actions.setCurrentNovel(refreshedNovel.novel);
                }
            } else {
                console.error("Error creating chapter:", response.error);
                alert("Failed to create chapter. Please try again.");
                
                // Revert to previous state if save failed
                const originalNovel = await api.getNovelById(novelId);
                if (originalNovel.success) {
                    setNovel(originalNovel.novel);
                    actions.setCurrentNovel(originalNovel.novel);
                }
            }
        } catch (error) {
            console.error("Error creating chapter:", error);
            alert("An error occurred while creating the chapter.");
            
            // Revert to previous state on error
            const originalNovel = await api.getNovelById(novelId);
            if (originalNovel.success) {
                setNovel(originalNovel.novel);
                actions.setCurrentNovel(originalNovel.novel);
            }
        } finally {
            setIsCreating(false);
        }
    };

    const novelChapters = novel?.chapters?.map(chapter => {
        return (
            <div className='chapter-summary-wrapper' key={chapter.id}>
                <ChapterSummary chapter={chapter} novel={state.currentNovel} />
            </div>
        );
    });
    
    return (
        <>
            {novelChapters}

            {novel?.chapters?.length === 0 ?
                <Flex className="tutorial-container" style={{ width: '100%', margin: '0 auto', maxWidth: '60%', marginTop: '25px', marginBottom: '-25px' }}>
                    <Flex className={"tutorial-text"}>
                        <img src={avatar} alt="Avatar" className="tutorial-avatar" />
                        <div>
                            At the moment, your novel is empty. Click the + button in the bottom right corner to add a new chapter.
                            <br />
                        </div>
                    </Flex>
                </Flex> : null}

            {novel?.chapters?.length === 0 && (
                <div style={{ textAlign: 'center', marginTop: '50px', fontSize: '1.5rem' }}>
                    No chapters yet. Click the button in the bottom right corner to add a new chapter.
                </div>
            )}
            
            {/* Reference element at the bottom */}
            <div ref={bottomRef} style={{ height: '1px', marginBottom: '100px' }}></div>
            
            {/* Add Chapter Button - Fixed at bottom right */}
            {novel?.type === 'fiction' &&
            <Tooltip content="Add New Chapter">
                <button 
                    onClick={createEmptyChapter}
                    disabled={isCreating}
                    style={{
                        position: 'fixed',
                        bottom: '30px',
                        right: '30px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: isCreating ? '#8a9b9d' : '#536264',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: 'none',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        cursor: isCreating ? 'default' : 'pointer',
                        fontSize: '24px',
                        zIndex: 1000,
                        transition: 'transform 0.2s, background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                        if (!isCreating) {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.backgroundColor = '#3e4a4c';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isCreating) {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.backgroundColor = '#536264';
                        }
                    }}
                >
                    {isCreating ? '...' : '+'}
                </button>
            </Tooltip>
        }
        </>
    );
};

export default Novel;